import React, {
  FunctionComponent,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import * as actions from 'data/jurisdictionArea/jurisdictionArea.actions';
import { JurisdictionAreaGridData } from 'data/jurisdictionArea/jurisdictionArea.types';
import * as selectors from 'data/jurisdictionArea/jurisdictionArea.selectors';
// import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import JurisdictionAreaForm from '../../components/JurisdictionAreaForm';
import { jurisdictionAreaTemplate } from '../../templates/jurisdictionAreaTemplate';
import { adaptTemplateVisibilityMode } from '../../utils/jurisdictionAreaTemplate.adapters';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  selectedJurisdictionArea: selectors.getSelectedJurisdictionArea(state),
});

const mapDispatch = {
  goToJurisdictionAreaList: actions.goToJurisdictionAreaList,
  goToJurisdictionAreaEdit: actions.goToJurisdictionAreaEdit,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const JurisdictionAreaView: FunctionComponent<Props> = ({
  selectedJurisdictionArea,
  history,
  goToJurisdictionAreaList,
  goToJurisdictionAreaEdit,
}: Props) => {
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(jurisdictionAreaTemplate, 'view'));
  const { t } = useTranslation();

  useEffect(() => {
    if (selectedJurisdictionArea != null && template != null) {
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      setTemplate(clonedTemplate);
    }
  }, [selectedJurisdictionArea]);

  const handleClickEdit = () => goToJurisdictionAreaEdit(history);
  const handleClickCancel = () => goToJurisdictionAreaList(history);
  const handleSelectionChanged = (event: SelectionChangedEvent) => { };

  const renderForm = () => {
    if (selectedJurisdictionArea && template) {
      return (
        <JurisdictionAreaForm
          onSelectionChanged={handleSelectionChanged}
          gridData={selectedJurisdictionArea}
          template={template}
          mode="view"
        />
      );
    }
  }

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleClickCancel}
          onClickClose={handleClickCancel}
          onClickEdit={handleClickEdit}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="JurisdictionAreaView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('jurisdictionArea.view.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(JurisdictionAreaView));
